// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — « Mes espaces » (GET /api/dispositifs/socle/moi/espaces) : mandats en cours par
// organisation, clés effectives qu'ils donnent, clés transverses attribuées explicitement.
// Sert au menu (Layout.jsx) et à l'affichage des boutons. Les boutons ne sont qu'une aide : le serveur
// contrôle chaque acte (mandat à la date, double regard, jamais sur soi) et peut toujours refuser.
// Mémoire courte (60 s) partagée entre le menu et les pages, pour ne pas rappeler l'API à chaque écran.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { HandHeart, ScanQrCode } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const DUREE_MS = 60 * 1000;
let memoire = { cle: null, instant: 0, valeur: null, promesse: null };

function charger(token, personneId, { forcer = false } = {}) {
  const frais = memoire.cle === personneId && memoire.valeur && Date.now() - memoire.instant < DUREE_MS;
  if (!forcer && frais) return Promise.resolve(memoire.valeur);
  if (!forcer && memoire.cle === personneId && memoire.promesse) return memoire.promesse;
  const promesse = axios.get('/api/dispositifs/socle/moi/espaces', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((r) => {
      const d = (r.data && r.data.data) || {};
      const valeur = {
        organisations: Array.isArray(d.organisations) ? d.organisations : [],
        cles_transverses: Array.isArray(d.cles_transverses) ? d.cles_transverses : [],
      };
      memoire = { cle: personneId, instant: Date.now(), valeur, promesse: null };
      return valeur;
    })
    .catch((e) => { memoire = { ...memoire, promesse: null }; throw e; });
  memoire = { ...memoire, cle: personneId, promesse };
  return promesse;
}

/** À appeler après un acte qui peut changer les mandats de la personne connectée. */
export function invaliderEspaces() { memoire = { cle: null, instant: 0, valeur: null, promesse: null }; }

/** Espaces de la personne connectée : { espaces, erreur, recharger }. */
export function useEspaces(actif = true) {
  const { token, employe } = useAuth();
  const [espaces, setEspaces] = useState(memoire.cle === employe?.id ? memoire.valeur : null);
  const [erreur, setErreur] = useState(null);
  const personneId = employe?.id || null;
  const recharger = useCallback((forcer = true) => {
    if (!personneId) return Promise.resolve(null);
    return charger(token, personneId, { forcer })
      .then((v) => { setEspaces(v); setErreur(null); return v; })
      .catch((e) => { setErreur(e); setEspaces(null); return null; });
  }, [token, personneId]);
  useEffect(() => { if (actif) recharger(false); }, [actif, recharger]);
  return { espaces, erreur, recharger };
}

/** Droits d'affichage pour une organisation (clés de mes mandats dans cette organisation + clés transverses). */
export function droitsDans(espaces, organisationId) {
  const org = (espaces?.organisations || []).find((o) => o.organisation_id === organisationId) || null;
  const cles = new Set([...(org?.cles || []), ...(espaces?.cles_transverses || [])]);
  return { a: (cle) => cles.has(cle), mandats: org?.mandats || [], organisation: org };
}

/**
 * Entrée du menu « Dispositifs sociaux » : visible avec un mandat en cours ou une clé transverse. Sans mandat, la
 * permission controler_cartes_employes (agent d'accueil, garde) donne l'entrée « Contrôle des cartes ».
 */
export function useEntreeDispositifs(actif) {
  const { hasPermission } = useAuth();
  const { espaces, erreur } = useEspaces(actif);
  if (!actif || (!espaces && !erreur)) return null;
  const controle = hasPermission && hasPermission('controler_cartes_employes');
  if (espaces && (espaces.organisations.length || espaces.cles_transverses.length)) {
    return { path: '/dispositifs', label: 'Dispositifs sociaux', Ico: HandHeart, groupe: 'avantages' };
  }
  return controle ? { path: '/dispositifs/controle-carte', label: 'Contrôle des cartes', Ico: ScanQrCode, groupe: 'avantages' } : null;
}

/** Ajoute l'entrée au menu : groupe « Prêts et avantages », ou menu à plat (salarié) juste après l'accueil. */
export function ajouterEntreeDispositifs(items, entree) {
  if (!entree) return items;
  if (items.every((i) => !i.groupe)) return [...items.slice(0, 1), { ...entree, groupe: undefined }, ...items.slice(1)];
  return [...items, entree];
}
